import { Observable, catchError, take, tap } from "rxjs";
import { DbCrudHelper, RowsAffected } from "./db-crud-helper";
import { TSLogActions, TSLogObjectTypes, TSLoggingService } from "./ts-logging.service";

import { Injectable } from "@angular/core";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { IDbHelper } from "../model/db-object.model";
import { DataServiceError } from "./data-service-error";
import { ILogService } from "./logs/log.interface";

@Injectable({
    providedIn: "root",
})
export class ClareWSCrudHelperService<T, D> {
    // helper: DbHelper<T, D>;

    //==========================New Methods================================
    // consider to re-use this class as parent
    // ====================================================================

    /**
     * @description call to create object
     * @param param object for creating
     * @param param.endpoint API endpoint to create new object
     * @param param.data data for new object
     * @param param.auditLogName
     * @param param.logData data for logging
     * @returns { Observable } return observable object when adding
     */
    create({
        endpoint,
        data,
        logData,
        auditLogName,
    }: {
        endpoint: string;
        data: T;
        logData: Record<string, any>;
        auditLogName?: string;
    }): Observable<D | null> {
        const dbo: D = this.helper.toDbObject(data);

        // remove when done migrating
        if (this.tsLoggingService instanceof TSLoggingService) {
            this.tsLoggingService.logEvent(null, this.helper.dbTypeName, TSLogObjectTypes.ssopgrest, TSLogActions.add, null, logData);
        }

        return this.crudHelper.makeRequest<D>(endpoint, dbo).pipe(
            take(1),
            tap(_ => this.saveAuditLog(logData, auditLogName)),
            catchError(DataServiceError.handleError())
        );
    }

    createFromAny({
        endpoint,
        data,
        logData,
        auditLogName,
    }: {
        endpoint: string;
        data: any;
        logData: Record<string, any>;
        auditLogName?: string;
    }) {
        // remove when done migrating
        if (this.tsLoggingService instanceof TSLoggingService) {
            this.tsLoggingService.logEvent(null, this.helper.dbTypeName, TSLogObjectTypes.ssopgrest, TSLogActions.add, null, logData);
        }
        return this.crudHelper.makeRequest<any>(endpoint, data).pipe(
            take(1),
            tap(_ => this.saveAuditLog(logData, auditLogName)),
            catchError(DataServiceError.handleError())
        );
    }

    /**
     * @description retrieving list data
     * @param param retrieving data params
     * @param param.endpoint API endpoint to create new object
     * @param param.data filter fields for retrieving data
     * @returns {Observable}  return observable list
     */
    retrieve({ endpoint, data }: { endpoint: string; data?: Record<string, any> }): Observable<D[] | null> {
        return this.crudHelper
            .makeRequest<D[]>(endpoint, data)
            .pipe(take(1), this.crudHelper.returnNullIfNotFound(), catchError(DataServiceError.handleError()));
    }

    /**
     * @description retrieving single data item
     * @param param retrieving data params
     * @param param.endpoint API endpoint to create new object
     * @param param.data filter fields for retrieving data
     * @param param.errorsToSuppress optional array of error messages to suppress
     * @returns {Observable}  return observable item
     */
    retrieveSingle({
        endpoint,
        data,
        errorsToSuppress,
    }: {
        endpoint: string;
        data?: Record<string, any>;
        errorsToSuppress?: string[];
    }): Observable<D | null> {
        return this.crudHelper
            .makeRequest<D>(endpoint, data, errorsToSuppress)
            .pipe(take(1), this.crudHelper.returnNullIfNotFound(), catchError(DataServiceError.handleError()));
    }

    /**
     * @description call to update object
     * @param param object for creating
     * @param param.endpoint API endpoint to create new object
     * @param param.data data for new object
     * @param param.auditLogName
     * @param param.logData data for logging
     * @returns { Observable } return observable object when updating
     */
    update({
        endpoint,
        data,
        logData,
        auditLogName,
    }: {
        endpoint: string;
        data: T;
        logData: Record<string, any>;
        auditLogName?: string;
    }): Observable<RowsAffected> {
        const dbo = this.helper.toDbObject(data);

        // remove when done migrating
        if (this.tsLoggingService instanceof TSLoggingService) {
            this.tsLoggingService.logEvent(null, this.helper.dbTypeName, TSLogObjectTypes.ssopgrest, TSLogActions.edit, null, logData);
        }
        return this.crudHelper.makeRequest<RowsAffected>(endpoint, dbo).pipe(
            take(1),
            tap(_ => this.saveAuditLog(logData, auditLogName)),
            this.crudHelper.throwIfZeroUpdates(),
            catchError(DataServiceError.handleError())
        );
    }

    /**
     * @description call to delete object
     * @param param object for creating
     * @param param.endpoint API endpoint to create new object
     * @param param.id object id to delete
     * @param param.auditLogName
     * @param param.logData data for logging
     * @returns { Observable } return observable object when updating
     */
    delete({
        endpoint,
        id,
        logData,
        auditLogName,
    }: {
        endpoint: string;
        id: string | number;
        logData: Record<string, any>;
        auditLogName?: string;
    }): Observable<RowsAffected> {
        return this.crudHelper.makeRequest<RowsAffected>(endpoint, { id }).pipe(
            take(1),
            tap(_ => {
                if (!!logData?.entityId || !!logData.id) {
                    this.saveAuditLog(logData, auditLogName, !!logData.id);
                }
            }),
            this.crudHelper.throwIfZeroUpdates(),
            catchError(DataServiceError.handleError())
        );
    }

    private saveAuditLog(logData: Record<string, any> | string, auditLogName: string, isEdit = false) {
        if (auditLogName && this.tsLoggingService instanceof AuditLogService) {
            const log = typeof logData === "object" && logData ? logData : { data: logData || "" };
            this.tsLoggingService.logEvent(auditLogName, log, isEdit);
        }
    }

    constructor(protected tsLoggingService: ILogService, protected crudHelper: DbCrudHelper, private helper: IDbHelper<T, D>) {}
}
